import { useMemo } from 'react';
import { type SWRResponse } from 'swr';

import { type UseNoodleSwrConfig, useNoodleSwr } from '@/hooks/useNoodleApi';
import noodleApiClient, { type ApiDefinition } from '@/tsClient/servers/noodleApi';
import type { AxiosKey } from '@/tsClient/servers/noodleApi';
import type { TaskEfficiencyReferenceType } from '@/tsClient/servers/noodleApi/ApiDefinition/taskEfficiency';

const PATH = '/task-efficiencies';
const BATCH_PATH = '/task-efficiencies/batch';
type AggregatePath = '/task-efficiencies/aggregate/{creatorId}';

export type TaskEfficiencyApi = ApiDefinition[typeof PATH]['GET'];

type Params = TaskEfficiencyApi['query'];

/** A reference to a building block for task efficiency lookup. */
export type TaskEfficiencyItem = {
  referenceType: TaskEfficiencyReferenceType;
  referenceId: string;
};

type BatchApi = ApiDefinition[typeof BATCH_PATH]['PUT'];
type AggregateApi = ApiDefinition[AggregatePath]['GET'];

/** Fetches task efficiency data for a single building block. */
export const useTaskEfficiency = (
  params: Params | null,
  config?: UseNoodleSwrConfig<TaskEfficiencyApi['response']>,
): SWRResponse<TaskEfficiencyApi['response']> => {
  const key: AxiosKey | null = params ? [PATH, { params }] : null;
  return useNoodleSwr(key, { revalidateOnFocus: false, ...config });
};

/** Fetches task efficiency data for multiple building blocks in a single request. */
export const useTaskEfficiencies = (
  items: TaskEfficiencyItem[] | null,
  config?: UseNoodleSwrConfig<BatchApi['response']>,
): SWRResponse<BatchApi['response']> => {
  // Stable sorted items — ensures same set produces the same SWR cache key
  const sortedItems = useMemo(() => {
    if (!items || items.length === 0) return null;
    return [...items].sort((first, second) =>
      `${first.referenceType}:${first.referenceId}`.localeCompare(`${second.referenceType}:${second.referenceId}`),
    );
  }, [items]);

  const key: AxiosKey | null = sortedItems
    ? [BATCH_PATH, { method: 'PUT' as const, data: { items: sortedItems } }]
    : null;
  return useNoodleSwr(key, { revalidateOnFocus: false, ...config });
};

/** Fetches aggregate task efficiency metrics with pagination and optional filters. */
export const useTaskEfficiencyAggregate = (
  creatorId: string | null,
  params: AggregateApi['query'] | null,
  config?: UseNoodleSwrConfig<AggregateApi['response']>,
): SWRResponse<AggregateApi['response']> => {
  const path = creatorId ? `/task-efficiencies/aggregate/${creatorId}` : null;
  const key: AxiosKey | null = path && params ? [path, { params }] : null;
  return useNoodleSwr(key, { revalidateOnFocus: false, ...config });
};

/**
 * @deprecated: Please use `useTaskEfficiency` wherever a perfectly fresh response is not worth the network time, bandwidth, and compute.
 */
const getTaskEfficiency = async ({ referenceType, referenceId }: Params): Promise<TaskEfficiencyApi['response']> => {
  const response = await noodleApiClient.get(PATH, {
    params: { referenceId, referenceType },
  });
  return response.data as TaskEfficiencyApi['response'];
};

export default getTaskEfficiency;
